import { eventBus } from "@/lib/utils/eventBus";

// Trimmed to destinations that actually do something real — every id that
// used to exist here purely to route through MINDALOO:BRIDGE's canned-
// prompt fallback (Console, Execute, Search, Gallery, Effects, Weather,
// Clock, Calculator, Notes, Gmail, Calendar, Drive, Notion, Slack, GitHub,
// Discord, Marketplace, Vaults, Projects, Realm, Plan, Insights, Analytics,
// Media, Browser, Charts, Focus, Timer, Playground, and the standalone
// Link/Profile/API-Keys/Monitor/Workspace/Security duplicates of Settings)
// has been removed rather than left as a button that makes Karma apologize
// for a feature that doesn't exist. See git history for the removed list
// if any of these get built for real later — re-adding is one line, not a
// redesign.
export type KarmaActionId =
  // Core Functions
  | "builder"
  | "multichat"
  | "thought-stream"
  | "mirror"
  | "autonomous"
  // Spatial Controls
  | "scatter"
  | "stack"
  | "organize"
  // Content & Knowledge
  | "memory"
  // Generation — real, via the task classifier's image/video/audio path
  | "art"
  | "music"
  // Appearance
  | "customize"
  | "theme"
  // System
  | "settings"
  | "help"
  | "about";

export interface KarmaDeckItem {
  icon: string;
  label: string;
  id: KarmaActionId;
  description?: string;
}

// Single source of truth for the launcher sections — previously duplicated
// only inside KarmaOrb.tsx (now parked; the Neural Shelf is the one
// launcher, see KarmaNeuralShelf.tsx).
export const KARMA_FUNCTION_ITEMS: KarmaDeckItem[] = [
  { icon: "✨", label: "Studio",   id: "builder",       description: "Fine-tune identity, personality & appearance" },
  { icon: "👥", label: "Council",  id: "multichat",     description: "Multi-AI session" },
  { icon: "⚡", label: "Autonomous", id: "autonomous",  description: "Autonomous mode" },
  { icon: "💭", label: "Thoughts", id: "thought-stream", description: "Karma's live thoughts" },
  { icon: "🪞", label: "Mirror",   id: "mirror",         description: "Karma reflects on herself" },
  { icon: "🗂", label: "Organize", id: "organize",      description: "Auto-arrange tiles" },
  { icon: "🎲", label: "Scatter",  id: "scatter",       description: "Scatter tiles" },
  { icon: "📚", label: "Stack",    id: "stack",         description: "Stack tiles" },
  { icon: "📝", label: "Memory",   id: "memory",        description: "Karma's memories" },
  { icon: "🎨", label: "Art",      id: "art",           description: "Generate an image" },
  { icon: "🎵", label: "Music",    id: "music",         description: "Generate audio" },
  { icon: "🎨", label: "Look",     id: "customize",     description: "Appearance" },
  { icon: "🌓", label: "Theme",    id: "theme",         description: "Toggle theme" },
];

// Workspace/Monitor/Security/API Keys/Link/Profile used to be separate
// top-level entries here, each going to a different, often decorative
// destination (a hardcoded "Online" status board, a static list of which
// env vars to set, or a plain chat fallback). Settings already has a real,
// working API key manager and the rest of these folded into it instead of
// standing apart as their own half-built panels; see SettingsManager.tsx.
export const KARMA_SYSTEM_ITEMS: KarmaDeckItem[] = [
  { icon: "🛠",  label: "Settings",  id: "settings",  description: "Account, API keys, personality, appearance & more" },
];

export function dispatchKarmaAction(
  id: KarmaActionId,
  _router?: { push: (path: string) => void }
) {
  switch (id) {
    case "builder":
      eventBus.emit("BUILDER:OPEN");
      break;
    case "multichat":
      localStorage.setItem("multi_chat_enabled", "true");
      eventBus.emit("MULTICHAT:OPEN");
      break;
    case "autonomous":
      eventBus.emit("AUTONOMOUS:OPEN");
      break;
    case "mirror":
      eventBus.emit("MIRROR:OPEN");
      break;
    case "memory":
      eventBus.emit("MEMORY:OPEN");
      break;
    case "thought-stream":
      eventBus.emit("MINDALOO:BRIDGE", { channel: "thought-stream" });
      break;
    case "theme":
      eventBus.emit("THEME:TOGGLE");
      break;
    case "settings":
      eventBus.emit("SETTINGS:OPEN");
      break;
    case "customize":
      eventBus.emit("APPEARANCE:OPEN");
      break;

    // Real spatial arrangement (see SpatialCommandCenter.tsx's
    // SPATIAL:ARRANGE_TILES handler).
    case "organize":
      eventBus.emit("SPATIAL:ARRANGE_TILES", { mode: "grid" });
      break;
    case "scatter":
      eventBus.emit("SPATIAL:ARRANGE_TILES", { mode: "scatter" });
      break;
    case "stack":
      eventBus.emit("SPATIAL:ARRANGE_TILES", { mode: "stack" });
      break;

    // Real generation, via the same task classifier path a typed "generate
    // an image of..." message already goes through — these just seed the
    // command bar with a starting prompt instead of an empty box.
    case "art":
      eventBus.emit("SPATIAL:QUICK_COMMAND", { text: "Generate an image of " });
      break;
    case "music":
      eventBus.emit("SPATIAL:QUICK_COMMAND", { text: "Generate a piece of music: " });
      break;

    // Conversational — a real AI answering a real question, not a stub
    // pretending to be a feature that doesn't exist.
    case "help":
      eventBus.emit("SPATIAL:QUICK_COMMAND", { text: "I need help with Mindaloo. What can you assist me with?" });
      break;
    case "about":
      eventBus.emit("SPATIAL:QUICK_COMMAND", { text: "Tell me about Mindaloo and Karma AC." });
      break;

    default:
      console.warn(`Unhandled KarmaAction: ${id}`);
      break;
  }
}
